import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { addDoc, Timestamp, collection } from "firebase/firestore";
import { Link } from "react-router-dom";
import { z } from "zod";
import { db } from "../firebase/config";
import { LEAD_TYPES, submitLeadEmail } from "../services/leadEmail";
import { Button } from "./ui/button";
import { Field, FieldError, FieldGroup, FieldLabel } from "./ui/field";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const DRIVING_EXPERIENCE_OPTIONS = [
  "Под 1 година",
  "1 – 3 години",
  "4 – 10 години",
  "Над 10 години",
];

const PURCHASE_PLAN_OPTIONS = [
  "Планирам да закупя автомобил през следващите 6 месеца",
  "Планирам да закупя автомобил през следващите 12 месеца",
  "Обмислям покупка, но нямам конкретен срок",
  "Не планирам покупка в момента",
];

export const createEventGameSchema = (collectSocialProfile = true) => z.object({
  fullName: z.string().trim().min(3, "Моля, въведете име и фамилия"),
  phone: z
    .string()
    .trim()
    .regex(
      /^(?:\+359|0)\s?\d{1,2}\s?\d{3}\s?\d{3,4}$/,
      "Моля, въведете валиден телефонен номер",
    ),
  email: z.string().trim().email("Моля, въведете валиден имейл адрес"),
  drivingExperience: z.string().trim().min(1, "Моля, изберете шофьорски опит"),
  purchasePlan: z.string().trim().min(1, "Моля, изберете план за покупка"),
  socialHandle: z.string().trim().optional(),
  consent: z.literal(true, {
    errorMap: () => ({
      message:
        "Необходимо е съгласие за обработка на данните за целите на играта",
    }),
  }),
  marketingConsent: z.boolean().default(false),
  socialShareBonus: z.boolean().default(false),
  website: z.string().optional(),
}).superRefine((data, ctx) => {
  if (collectSocialProfile && data.socialShareBonus && !data.socialHandle?.trim()) {
    ctx.addIssue({
      code: "custom",
      path: ["socialHandle"],
      message:
        "Моля, въведете потребителско име във Facebook или Instagram за проверка на двойния шанс",
    });
  }
});

function CheckboxField({ id, label, error, register, required = false }) {
  return (
    <Field data-invalid={!!error}>
      <label
        htmlFor={id}
        className="flex items-start gap-3 px-4 py-3 text-sm leading-6 transition-colors bg-white border cursor-pointer rounded-2xl border-slate-200 text-slate-700 hover:border-slate-300"
      >
        <input
          id={id}
          type="checkbox"
          className="w-4 h-4 mt-1 rounded border-slate-300 text-fordBlue focus:ring-fordBlue"
          {...register(id)}
        />
        <span>
          {label}
          {required ? <span className="text-red-600"> *</span> : null}
        </span>
      </label>
      <FieldError>{error?.message}</FieldError>
    </Field>
  );
}

function EventGameForm({ campaign }) {
  const collectSocialProfile = campaign.collectSocialProfile !== false;
  const socialShareBonusEnabled = campaign.socialShareBonusEnabled !== false;
  const formSchema = useMemo(() => createEventGameSchema(collectSocialProfile), [collectSocialProfile]);
  const [submitting, setSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const defaultValues = useMemo(
    () => ({
      fullName: "",
      phone: "",
      email: "",
      drivingExperience: "",
      purchasePlan: "",
      socialHandle: "",
      consent: false,
      marketingConsent: false,
      socialShareBonus: false,
      website: "",
    }),
    [],
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const onSubmit = async (data) => {
    if (data.website) {
      return;
    }

    setSubmitting(true);

    try {
      await addDoc(collection(db, campaign.collectionName), {
        campaignSlug: campaign.slug,
        campaignName: campaign.name,
        eventDate: campaign.eventDate,
        fullName: data.fullName ?? "",
        phone: data.phone ?? "",
        email: data.email ?? "",
        drivingExperience: data.drivingExperience ?? "",
        purchasePlan: data.purchasePlan ?? "",
        socialHandle: data.socialHandle ?? "",
        consent: Boolean(data.consent),
        marketingConsent: Boolean(data.marketingConsent),
        socialShareBonus: socialShareBonusEnabled && Boolean(data.socialShareBonus),
        sourceUrl: window.location.href,
        date: Timestamp.fromDate(new Date()),
      });

      await submitLeadEmail({
        type: LEAD_TYPES.CAMPAIGN_OFFER,
        customer: {
          name: data.fullName ?? "",
          email: data.email ?? "",
          phone: data.phone ?? "",
        },
        dealership: {
          email: "",
          label: "",
        },
        vehicle: {
          model: campaign.name,
        },
        campaign: {
          slug: campaign.slug,
        },
        source: {
          pageUrl: window.location.href,
        },
        title: "Регистрация за игра - Ford Event Game",
        message: [
          `Шофьорски опит: ${data.drivingExperience ?? "-"}`,
          `План за покупка: ${data.purchasePlan ?? "-"}`,
          ...(collectSocialProfile ? [`Социален профил: ${data.socialHandle ?? "-"}`] : []),
          `Маркетинг съгласие: ${data.marketingConsent ? "Да" : "Не"}`,
          ...(socialShareBonusEnabled ? [`Двоен шанс: ${data.socialShareBonus ? "Да" : "Не"}`] : []),
        ].join("\n"),
      });

      reset(defaultValues);
      setIsSubmitted(true);
    } catch (error) {
      console.error("Грешка при изпращане на формата:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedDrivingExperience = watch("drivingExperience");
  const selectedPurchasePlan = watch("purchasePlan");
  const socialShareBonus = watch("socialShareBonus");

  if (isSubmitted) {
    return (
      <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_30px_90px_rgba(15,23,42,0.08)]">
        <p className="text-sm font-semibold uppercase tracking-[0.24em] text-fordBlue">
          Успешна регистрация
        </p>
        <h3 className="mt-3 text-3xl uppercase font-fordBlack text-slate-900">
          Благодарим Ви за участието
        </h3>
        <p className="max-w-2xl mt-4 text-base leading-7 text-slate-600">
          Регистрацията Ви беше приета успешно. Ако желаете, можете да попълните
          нова форма за друг участник.
        </p>
        <Button
          type="button"
          onClick={() => setIsSubmitted(false)}
          className="mt-6 h-12 rounded-full bg-fordBlue px-6 text-white hover:bg-[#001c7a]"
        >
          Нова регистрация
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex h-full flex-col rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_30px_90px_rgba(15,23,42,0.08)] sm:p-8"
      noValidate
    >
      <input
        type="text"
        tabIndex="-1"
        autoComplete="off"
        className="hidden"
        {...register("website")}
      />

      <div className="mb-8">
        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-fordBlue">
          Регистрация за участие
        </p>
        {/* <h2 className="mt-3 text-3xl leading-none uppercase font-fordBlack text-slate-900">
          Попълнете формата
        </h2> */}
        <p className="mt-4 text-sm leading-6 text-slate-600">
          Полетата, отбелязани със звезда, са задължителни.
        </p>
      </div>

      <FieldGroup className="flex-1 gap-5">
        <Field data-invalid={!!errors.fullName}>
          <FieldLabel htmlFor="fullName">
            Име и фамилия <span className="text-red-600">*</span>
          </FieldLabel>
          <Input
            id="fullName"
            className="px-4 min-h-12 rounded-xl border-slate-300"
            placeholder="Въведете име и фамилия"
            aria-invalid={!!errors.fullName}
            {...register("fullName")}
          />
          <FieldError>{errors.fullName?.message}</FieldError>
        </Field>

        <Field data-invalid={!!errors.phone}>
          <FieldLabel htmlFor="phone">
            Телефон <span className="text-red-600">*</span>
          </FieldLabel>
          <Input
            id="phone"
            className="px-4 min-h-12 rounded-xl border-slate-300"
            placeholder="Например 0888 123 456"
            aria-invalid={!!errors.phone}
            {...register("phone")}
          />
          <FieldError>{errors.phone?.message}</FieldError>
        </Field>

        <Field data-invalid={!!errors.email}>
          <FieldLabel htmlFor="email">
            Имейл адрес <span className="text-red-600">*</span>
          </FieldLabel>
          <Input
            id="email"
            type="email"
            className="px-4 min-h-12 rounded-xl border-slate-300"
            placeholder="Въведете имейл адрес"
            aria-invalid={!!errors.email}
            {...register("email")}
          />
          <FieldError>{errors.email?.message}</FieldError>
        </Field>

        <Field data-invalid={!!errors.drivingExperience}>
          <FieldLabel>
            Шофьорски опит <span className="text-red-600">*</span>
          </FieldLabel>
          <input
            type="hidden"
            value={selectedDrivingExperience}
            {...register("drivingExperience")}
          />
          <Select
            value={selectedDrivingExperience}
            onValueChange={(value) =>
              setValue("drivingExperience", value, { shouldValidate: true })
            }
          >
            <SelectTrigger
              className="w-full px-4 min-h-12 rounded-xl border-slate-300"
              aria-invalid={!!errors.drivingExperience}
            >
              <SelectValue placeholder="Изберете шофьорски опит" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {DRIVING_EXPERIENCE_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <FieldError>{errors.drivingExperience?.message}</FieldError>
        </Field>

        <Field data-invalid={!!errors.purchasePlan}>
          <FieldLabel>
            Планове за покупка на автомобил{" "}
            <span className="text-red-600">*</span>
          </FieldLabel>
          <input
            type="hidden"
            value={selectedPurchasePlan}
            {...register("purchasePlan")}
          />
          <Select
            value={selectedPurchasePlan}
            onValueChange={(value) =>
              setValue("purchasePlan", value, { shouldValidate: true })
            }
          >
            <SelectTrigger
              className="w-full px-4 text-left min-h-12 rounded-xl border-slate-300"
              aria-invalid={!!errors.purchasePlan}
            >
              <SelectValue placeholder="Изберете отговор" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {PURCHASE_PLAN_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <FieldError>{errors.purchasePlan?.message}</FieldError>
        </Field>

        <CheckboxField
          id="consent"
          label="Съгласен/на съм предоставените от мен данни да бъдат обработвани за целите на играта."
          error={errors.consent}
          register={register}
          required
        />

        <CheckboxField
          id="marketingConsent"
          label="Желая да получавам информация за нови модели, оферти, събития и промоции от Ford."
          error={errors.marketingConsent}
          register={register}
        />

        {socialShareBonusEnabled && <CheckboxField
          id="socialShareBonus"
          label="Участвам с двоен шанс за награда, като ще публикувам или вече съм публикувал/а снимка във Facebook или Instagram и съм отбелязал/а @FordBulgaria."
          error={errors.socialShareBonus}
          register={register}
        />}

        {collectSocialProfile && <Field data-invalid={!!errors.socialHandle}>
          <FieldLabel htmlFor="socialHandle">
            Потребителско име във Facebook/Instagram
            {socialShareBonus ? <span className="text-red-600"> *</span> : null}
          </FieldLabel>
          <Input
            id="socialHandle"
            className="px-4 min-h-12 rounded-xl border-slate-300"
            placeholder="Например @username или линк към профил"
            aria-invalid={!!errors.socialHandle}
            {...register("socialHandle")}
          />
          <FieldError>{errors.socialHandle?.message}</FieldError>
        </Field>}
      </FieldGroup>

      <Button
        type="submit"
        disabled={submitting}
        className="mt-8 h-12 w-full rounded-full bg-fordBlue px-6 text-sm font-semibold uppercase tracking-[0.18em] text-white hover:bg-[#001c7a]"
      >
        {submitting ? "Изпращане..." : "Участвай сега"}
      </Button>

      <p className="mt-5 text-xs leading-5 text-slate-500">
        Победителите ще бъдат изтеглени в срок до 3 работни дни след приключване
        на събитието, а организаторът ще се свърже с тях в срок до 5 работни дни
        след тегленето с цел уточняване на начина за получаване на наградите.
        Организаторът си запазва правото да публикува имената на печелившите
        след приключване на играта.
      </p>
      <p className="mt-2 text-xs leading-5 text-slate-500">
        С участието си приемате{" "}
        <Link
          to={campaign.termsPath || "/fordeventgametermsandconditions"}
          className="text-fordBlue underline underline-offset-4 hover:opacity-80"
        >
          Общите условия на играта
        </Link>
        .
      </p>
    </form>
  );
}

export default EventGameForm;
